<!-- Detect & Resolve Duplicate Transactions Modal -->
<div class="modal-header border-0 align-items-center px-4 pt-4 pb-2">
    <h5 class="modal-title text-primary d-flex align-items-center gap-3 m-0">
        <div class="confirmation-icon bg-primary-subtle text-primary rounded-3 d-flex align-items-center justify-content-center"
            style="width: 40px; height: 40px;">
            <i class="ri-search-eye-line fs-4"></i>
        </div>
        <span class="fs-5 fw-semibold">
            {{ 'DUPLICATES.MODAL_TITLE' | translate }}
        </span>
    </h5>
    <button type="button" class="btn-close" aria-label="Close"
        [disabled]="status === 'submitting'" (click)="onCancel()"></button>
</div>

<hr class="my-3 mx-4">

<!-- Stepper — hidden on success state; once the journey is done, the stepper is noise -->
@if (status !== 'done') {
    <div class="duplicate-stepper px-4 pb-3 pt-1">
        <div class="d-flex align-items-center gap-2">
            <div class="step-pill" [class.active]="step === 1" [class.done]="step > 1">
                <span class="step-index">1</span>
                <span class="step-label">{{ 'DUPLICATES.STEP_RANGE' | translate }}</span>
            </div>
            <div class="step-bar" [class.filled]="step > 1"></div>
            <div class="step-pill" [class.active]="step === 2" [class.done]="step > 2" [class.locked]="step < 2">
                <span class="step-index">2</span>
                <span class="step-label">{{ 'DUPLICATES.STEP_REVIEW' | translate }}</span>
            </div>
            <div class="step-bar" [class.filled]="step > 2"></div>
            <div class="step-pill" [class.active]="step === 3" [class.locked]="step < 3">
                <span class="step-index">3</span>
                <span class="step-label">{{ 'DUPLICATES.STEP_CONFIRM' | translate }}</span>
            </div>
        </div>
    </div>
}

<div class="modal-body py-4 px-4">

    <!-- ============ STEP 1 — RANGE ============ -->
    @if (step === 1) {
        <form [formGroup]="rangeForm" (ngSubmit)="submitDetect()">
            <!-- Bank account context — shown up-front so the user knows which account this range applies to -->
            <div class="card shadow-sm border-0 rounded-4 mx-auto bg-white mb-4" style="max-width: 480px;">
                <div class="card-header bg-primary-subtle text-dark py-3 border-0 rounded-top-4 text-start">
                    <h6 class="fw-bold mb-0 text-primary">{{ 'SOFT_PURGE.BANK_ACCOUNT_DETAILS' | translate }}</h6>
                </div>
                <div class="card-body p-4">
                    <div class="mb-3 d-flex align-items-center justify-content-between gap-3">
                        <div class="text-start">
                            <h6 class="text-primary mb-1 small text-uppercase">{{ 'SOFT_PURGE.ACCOUNT_NAME' | translate }}</h6>
                            <p class="fw-bold text-dark fs-6 mb-0">{{ config?.bankAccountDetails?.accountName }}</p>
                        </div>
                        <div class="d-flex flex-column align-items-end">
                            <img [src]="config?.bankAccountDetails?.bankLogoUrl" alt="bank logo"
                                class="h-32 object-fit-contain mb-1">
                            <div class="text-end small text-secondary" style="max-width: 180px; word-wrap: break-word;">
                                {{ config?.bankAccountDetails?.bankName }}
                            </div>
                        </div>
                    </div>
                    <hr class="my-3 opacity-25">
                    <div class="row g-2">
                        <div class="col-12 d-flex justify-content-between">
                            <span class="text-secondary small">{{ 'SOFT_PURGE.ACCOUNT_ID' | translate }}</span>
                            <span class="fw-semibold text-dark small">{{ config?.bankAccountDetails?.accountId }}</span>
                        </div>
                        <div class="col-12 d-flex justify-content-between">
                            <span class="text-secondary small">{{ 'SOFT_PURGE.ALIAS' | translate }}</span>
                            <span class="fw-semibold text-dark small">{{ config?.bankAccountDetails?.alias }}</span>
                        </div>
                        <div class="col-12 d-flex justify-content-between">
                            <span class="text-secondary small">{{ 'SOFT_PURGE.ACCOUNT_NUMBER' | translate }}</span>
                            <span class="fw-semibold text-dark small">
                                {{ config?.bankAccountDetails?.accountNumber | numberMaskPipe:4 }}
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Date Range card — mirrors the Sync Merchant Statement modal for UI/UX consistency -->
            <div class="card border-0 shadow-sm rounded-4 mx-auto bg-white mb-4 text-start" style="max-width: 480px;">
                <div class="card-header bg-info-subtle text-info py-3 border-0 rounded-top-4 d-flex align-items-center gap-2">
                    <i class="ri-calendar-event-line"></i>
                    <h6 class="fw-bold mb-0">
                        {{ 'DUPLICATES.RANGE_LABEL' | translate }}
                    </h6>
                </div>
                <div class="card-body p-4">
                    <p class="text-muted small mb-3">
                        {{ 'DUPLICATES.RANGE_HELPER' | translate }}
                    </p>

                    <div class="dashboard-controls d-flex flex-wrap align-items-center justify-content-center gap-1">
                        <div class="date-picker-group control-group">
                            <div class="d-flex flex-column flex-sm-row gap-2">
                                <!-- Date From -->
                                <div class="input-group input-group-md">
                                    <input id="duplicateFromDate" class="form-control flatpickr-input" type="text"
                                        mwlFlatpickr
                                        [options]="flatpickrFromOptions"
                                        [dateFormat]="'Y-m-d'"
                                        formControlName="fromDate"
                                        [class.is-invalid]="rangeForm.get('fromDate')?.touched && rangeForm.get('fromDate')?.invalid"
                                        [placeholder]="'DUPLICATES.FROM_DATE' | translate">
                                    <div class="input-group-text text-primary">
                                        <i class="ri-calendar-2-line"></i>
                                    </div>
                                </div>

                                <!-- Date To -->
                                <div class="input-group input-group-md">
                                    <input id="duplicateToDate" class="form-control flatpickr-input" type="text"
                                        mwlFlatpickr
                                        [options]="flatpickrToOptions"
                                        [dateFormat]="'Y-m-d'"
                                        formControlName="toDate"
                                        [class.is-invalid]="rangeForm.get('toDate')?.touched && rangeForm.get('toDate')?.invalid"
                                        [placeholder]="'DUPLICATES.TO_DATE' | translate">
                                    <div class="input-group-text text-primary">
                                        <i class="ri-calendar-2-line"></i>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    @if (rangeError) {
                        <div class="alert alert-warning d-flex align-items-center mb-0 mt-3 py-2 px-3">
                            <i class="ri-alert-line me-2"></i>
                            <small class="mb-0">{{ rangeError }}</small>
                        </div>
                    }
                </div>
            </div>

            @if (status === 'error') {
                <div class="alert alert-danger mt-3 mb-0 d-flex align-items-center mx-auto" style="max-width: 480px;">
                    <i class="ri-error-warning-line me-2"></i>
                    <span>{{ 'DUPLICATES.DETECT_ERROR' | translate }}</span>
                </div>
            }
        </form>
    }

    <!-- ============ STEP 2 — REVIEW ============ -->
    @if (step === 2) {
        <!-- Summary strip -->
        @if (detectResponse) {
            <div class="summary-strip d-flex flex-wrap gap-2 mb-3">
                <div class="summary-chip">
                    <span class="chip-label">{{ 'DUPLICATES.SCANNED' | translate }}</span>
                    <span class="chip-value">{{ detectResponse.totalTransactionsScanned | number }}</span>
                </div>
                <div class="summary-chip info">
                    <span class="chip-label">{{ 'DUPLICATES.GROUPS_FOUND' | translate }}</span>
                    <span class="chip-value">{{ detectResponse.duplicateGroupsFound | number }}</span>
                </div>
                <div class="summary-chip danger">
                    <span class="chip-label">{{ 'DUPLICATES.TOTAL_DUPLICATES' | translate }}</span>
                    <span class="chip-value">{{ detectResponse.totalDuplicateTransactions | number }}</span>
                </div>
            </div>
        }

        <!-- Empty state -->
        @if (status === 'empty') {
            <div class="empty-state text-center py-5">
                <div class="empty-icon mx-auto mb-3">
                    <i class="ri-shield-check-line"></i>
                </div>
                <h6 class="fw-bold mb-1">{{ 'DUPLICATES.EMPTY_TITLE' | translate }}</h6>
                <p class="text-muted mb-0">{{ 'DUPLICATES.EMPTY_MESSAGE' | translate }}</p>
            </div>
        }

        <!-- Groups -->
        @if (status === 'reviewing' && detectResponse?.duplicateGroups?.length) {
            <div class="group-list">
                @for (group of detectResponse.duplicateGroups; track trackGroup($index, group); let i = $index) {
                    @let isExpanded = expandedGroupIndex === i;
                    <div class="group-card" [class.expanded]="isExpanded">
                        <button type="button" class="group-header"
                            [attr.aria-expanded]="isExpanded"
                            (click)="toggleGroup(i)">
                            <div class="group-meta">
                                <span class="group-date">
                                    <i class="ri-calendar-line me-1"></i>{{ group.transactionDate }}
                                </span>
                                <span class="group-desc" [title]="group.transactionInformation">
                                    {{ group.transactionInformation }}
                                </span>
                            </div>
                            <div class="group-facts d-flex align-items-center gap-2">
                                <span class="amount-chip"
                                    [class.credit]="group.creditDebitIndicator === 'CREDIT'"
                                    [class.debit]="group.creditDebitIndicator === 'DEBIT'">
                                    {{ group.creditDebitIndicator === 'CREDIT' ? '+' : '−' }}
                                    {{ group.amount | number:'1.2-2' }}
                                </span>
                                <span class="count-badge">×{{ group.count }}</span>
                                <i class="ri-arrow-down-s-line chevron"></i>
                            </div>
                        </button>

                        <!-- Collapsible wrapper uses grid-row trick for smooth height animation -->
                        <div class="group-body-wrapper" [attr.aria-hidden]="!isExpanded">
                            <div class="group-body">
                                <div class="group-body-scroll">
                                    <table class="table table-sm align-middle mb-0 group-table">
                                        <thead>
                                            <tr>
                                                <th>{{ 'DUPLICATES.TXN_ID' | translate }}</th>
                                                <th>{{ 'DUPLICATES.CREATED' | translate }}</th>
                                                <th class="text-end">{{ 'DUPLICATES.STATUS' | translate }}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            @for (tx of group.transactions; track trackTx($index, tx)) {
                                                <tr [class.kept-row]="tx.kept">
                                                    <td>
                                                        <span class="txn-id" [title]="tx.transactionId">{{ tx.transactionId }}</span>
                                                    </td>
                                                    <td class="text-muted small text-nowrap">{{ tx.createdDate }}</td>
                                                    <td class="text-end">
                                                        @if (tx.kept) {
                                                            <span class="status-pill kept">
                                                                <i class="ri-shield-check-line"></i>
                                                                {{ 'DUPLICATES.KEPT' | translate }}
                                                            </span>
                                                        } @else {
                                                            <span class="status-pill remove">
                                                                <i class="ri-close-circle-line"></i>
                                                                {{ 'DUPLICATES.WILL_REMOVE' | translate }}
                                                            </span>
                                                        }
                                                    </td>
                                                </tr>
                                            }
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                }
            </div>
        }
    }

    <!-- ============ STEP 3 — CONFIRM ============ -->
    @if (step === 3) {
        <!-- Success state — rich resolution summary -->
        @if (status === 'done') {
            <div class="resolution-success text-center">
                <div class="empty-icon success mx-auto mb-3">
                    <i class="ri-check-double-line"></i>
                </div>
                <h5 class="fw-bold mb-1 text-success">{{ 'DUPLICATES.DONE_TITLE' | translate }}</h5>
                <p class="text-muted mb-4">{{ 'DUPLICATES.DONE_MESSAGE' | translate }}</p>

                <div class="resolution-summary mx-auto" style="max-width: 480px;">
                    <div class="res-stat">
                        <div class="res-stat-icon bg-primary-subtle text-primary">
                            <i class="ri-stack-line"></i>
                        </div>
                        <div class="res-stat-meta text-start">
                            <span class="res-stat-label">{{ 'DUPLICATES.GROUPS_RESOLVED' | translate }}</span>
                            <span class="res-stat-value">{{ resolveResponse?.duplicateGroupsFound | number }}</span>
                        </div>
                    </div>

                    <div class="res-stat">
                        <div class="res-stat-icon bg-danger-subtle text-danger">
                            <i class="ri-delete-bin-6-line"></i>
                        </div>
                        <div class="res-stat-meta text-start">
                            <span class="res-stat-label">{{ 'DUPLICATES.TRANSACTIONS_REMOVED' | translate }}</span>
                            <span class="res-stat-value text-danger">{{ resolveResponse?.duplicateTransactionsRemoved | number }}</span>
                        </div>
                    </div>

                </div>
            </div>
        } @else {
            <div class="alert alert-danger d-flex align-items-start mb-4">
                <i class="ri-error-warning-line fs-4 me-3 mt-1"></i>
                <div>
                    <h6 class="alert-heading fw-bold mb-2">{{ 'DUPLICATES.CONFIRM_WARNING_TITLE' | translate }}</h6>
                    <p class="mb-0">{{ 'DUPLICATES.CONFIRM_WARNING_MESSAGE' | translate }}</p>
                </div>
            </div>

            <!-- Full bank context + resolve summary -->
            <div class="card shadow-sm border-0 rounded-4 mx-auto bg-white mb-4" style="max-width: 480px;">
                <div class="card-header bg-primary-subtle text-dark py-3 border-0 rounded-top-4 text-start">
                    <h6 class="fw-bold mb-0 text-primary">{{ 'SOFT_PURGE.BANK_ACCOUNT_DETAILS' | translate }}</h6>
                </div>
                <div class="card-body p-4">
                    <div class="mb-3 d-flex align-items-center justify-content-between gap-3">
                        <div class="text-start">
                            <h6 class="text-primary mb-1 small text-uppercase">{{ 'SOFT_PURGE.ACCOUNT_NAME' | translate }}</h6>
                            <p class="fw-bold text-dark fs-6 mb-0">{{ config?.bankAccountDetails?.accountName }}</p>
                        </div>
                        <div class="d-flex flex-column align-items-end">
                            <img [src]="config?.bankAccountDetails?.bankLogoUrl" alt="bank logo"
                                class="h-32 object-fit-contain mb-1">
                            <div class="text-end small text-secondary" style="max-width: 180px; word-wrap: break-word;">
                                {{ config?.bankAccountDetails?.bankName }}
                            </div>
                        </div>
                    </div>
                    <hr class="my-3 opacity-25">
                    <div class="row g-2">
                        <div class="col-12 d-flex justify-content-between">
                            <span class="text-secondary small">{{ 'SOFT_PURGE.ACCOUNT_ID' | translate }}</span>
                            <span class="fw-semibold text-dark small">{{ config?.bankAccountDetails?.accountId }}</span>
                        </div>
                        <div class="col-12 d-flex justify-content-between">
                            <span class="text-secondary small">{{ 'SOFT_PURGE.ALIAS' | translate }}</span>
                            <span class="fw-semibold text-dark small">{{ config?.bankAccountDetails?.alias }}</span>
                        </div>
                        <div class="col-12 d-flex justify-content-between">
                            <span class="text-secondary small">{{ 'SOFT_PURGE.ACCOUNT_NUMBER' | translate }}</span>
                            <span class="fw-semibold text-dark small">
                                {{ config?.bankAccountDetails?.accountNumber | numberMaskPipe:4 }}
                            </span>
                        </div>
                    </div>

                    <hr class="my-3 opacity-25">
                    <div class="row g-2">
                        <div class="col-12 d-flex justify-content-between">
                            <span class="text-secondary small">{{ 'DUPLICATES.RANGE_LABEL' | translate }}</span>
                            <span class="fw-semibold text-dark small">
                                {{ rangeForm.value.fromDate }} → {{ rangeForm.value.toDate }}
                            </span>
                        </div>
                        <div class="col-12 d-flex justify-content-between">
                            <span class="text-secondary small">{{ 'DUPLICATES.TOTAL_DUPLICATES' | translate }}</span>
                            <span class="fw-semibold text-danger small">
                                {{ detectResponse?.totalDuplicateTransactions | number }}
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            <div [formGroup]="confirmForm" class="text-center">
                <label for="duplicateConfirmationInput" class="form-label text-danger fw-semibold">
                    {{ 'SOFT_PURGE.CONFIRMATION_LABEL' | translate }} "{{ expectedConfirmationText }}" {{ 'SOFT_PURGE.TO_CONFIRM' | translate }}
                </label>
                <input type="text" id="duplicateConfirmationInput" formControlName="confirmationInput"
                    class="form-control"
                    [class.is-invalid]="showConfirmationError"
                    [placeholder]="expectedConfirmationText" autocomplete="off" />
                @if (showConfirmationError) {
                    <div class="invalid-feedback d-block">
                        {{ 'SOFT_PURGE.VALIDATION_ERROR' | translate }}
                    </div>
                }
            </div>
        }
    }
</div>

<!-- Modal Footer -->
<div class="modal-footer py-3 bg-light justify-content-between">
    <div>
        @if (step === 2 && status !== 'submitting' && status !== 'done') {
            <button type="button" class="btn btn-link back-link d-inline-flex align-items-center p-0"
                (click)="backToRange()">
                <i class="ri-arrow-left-s-line"></i>
                <span>{{ 'DUPLICATES.CHANGE_RANGE' | translate }}</span>
            </button>
        }
        @if (step === 3 && status !== 'submitting' && status !== 'done') {
            <button type="button" class="btn btn-link back-link d-inline-flex align-items-center p-0"
                (click)="backToReview()">
                <i class="ri-arrow-left-s-line"></i>
                <span>{{ 'DUPLICATES.BACK' | translate }}</span>
            </button>
        }
    </div>

    <div class="d-flex gap-2">
        @if (status === 'done') {
            <!-- Single primary action on success — focuses the user on one exit -->
            <button type="button" class="btn btn-success px-4"
                (click)="onCancel()">
                {{ 'COMMON.DONE' | translate }}
            </button>
        } @else {
            <button type="button" class="btn btn-secondary"
                [disabled]="status === 'submitting'" (click)="onCancel()">
                {{ status === 'empty' ? ('COMMON.CLOSE' | translate) : ('COMMON.CANCEL' | translate) }}
            </button>
        }

        @if (step === 1) {
            <button type="button" class="btn btn-primary px-4 d-inline-flex align-items-center justify-content-center"
                [disabled]="!canSubmitRange" (click)="submitDetect()">
                @if (status === 'scanning') {
                    <span class="spinner-border spinner-border-sm me-2"></span>
                    <span>{{ 'DUPLICATES.SCANNING' | translate }}</span>
                } @else {
                    <i class="ri-search-line me-2"></i>
                    <span>{{ 'DUPLICATES.DETECT' | translate }}</span>
                }
            </button>
        }

        @if (step === 2 && status === 'reviewing') {
            <button type="button" class="btn btn-primary px-4 d-inline-flex align-items-center justify-content-center proceed-btn"
                (click)="goToConfirm()">
                <span>{{ 'DUPLICATES.PROCEED_TO_CONFIRM' | translate }}</span>
                <i class="ri-arrow-right-s-line ms-2"></i>
            </button>
        }

        @if (step === 3 && status !== 'done') {
            <button type="button" class="btn btn-danger px-4 d-inline-flex align-items-center justify-content-center"
                [disabled]="!isConfirmationValid || status === 'submitting'"
                (click)="submitResolve()">
                @if (status === 'submitting') {
                    <span class="spinner-border spinner-border-sm me-2"></span>
                    <span>{{ 'COMMON.PROCESSING' | translate }}</span>
                } @else {
                    <i class="ri-shield-check-line me-2"></i>
                    <span>{{ 'DUPLICATES.CONFIRM_RESOLVE' | translate }}</span>
                }
            </button>
        }
    </div>
</div>
